import React, { useState, useEffect } from 'react';
import { 
  FileText, Filter, Download, Calendar,
  TrendingUp, DollarSign, Users, Package
} from 'lucide-react';
import api from '../../services/api';
import { useExport } from '../../hooks/useExport';
import DateRangePicker from '../Form/DateRangePicker';
//import Select from '../Form/Select';
import { format } from 'date-fns';

const ReportBuilder = () => {
  const { exportData, exportFinancialReport, exporting } = useExport();
  const [reportType, setReportType] = useState('clientes');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  const [filters, setFilters] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    {
      value: 'clientes',
      label: 'Relatório de Clientes',
      icon: Users,
      endpoint: '/clientes',
      columns: [
        { header: 'Nome', accessorKey: 'nome' },
        { header: 'CPF/CNPJ', accessorKey: 'cpfCnpj' },
        { header: 'Email', accessorKey: 'email' },
        { header: 'Status', accessorKey: 'ativo' }
      ]
    },
    {
      value: 'contratos',
      label: 'Relatório de Contratos',
      icon: Package,
      endpoint: '/contratos',
      columns: [
        { header: 'Cliente', accessorKey: 'cliente.nome' },
        { header: 'Plano', accessorKey: 'plano.nomePlano' },
        { header: 'Valor', accessorKey: 'valorMensalidade' },
        { header: 'Status', accessorKey: 'status' }
      ]
    },
    {
      value: 'financeiro',
      label: 'Relatório Financeiro',
      icon: DollarSign,
      endpoint: '/movimentacoes',
      columns: [
        { header: 'Data', accessorKey: 'dataVencimento' },
        { header: 'Descrição', accessorKey: 'descricao' },
        { header: 'Tipo', accessorKey: 'tipo' },
        { header: 'Valor', accessorKey: 'valor' },
        { header: 'Status', accessorKey: 'status' }
      ]
    },
    {
      value: 'inadimplencia',
      label: 'Relatório de Inadimplência',
      icon: TrendingUp,
      endpoint: '/clientes/inadimplentes',
      columns: [
        { header: 'Cliente', accessorKey: 'nome' },
        { header: 'CPF/CNPJ', accessorKey: 'cpfCnpj' },
        { header: 'Total Débito', accessorKey: 'totalDebito' },
        { header: 'Boletos Vencidos', accessorKey: 'boletos.length' }
      ]
    }
  ];

  const currentReport = reportTypes.find(r => r.value === reportType);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        dataInicio: format(dateRange.startDate, 'yyyy-MM-dd'),
        dataFim: format(dateRange.endDate, 'yyyy-MM-dd'),
        limit: 10
      };

      const response = await api.get(currentReport.endpoint, { params });
      setPreviewData(response.data.data || response.data);
    } catch (error) {
      console.error('Erro ao carregar preview:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, [reportType, dateRange, filters]);

  const handleExport = (format) => {
    const config = {
      endpoint: currentReport.endpoint,
      params: {
        ...filters,
        dataInicio: format(dateRange.startDate, 'yyyy-MM-dd'),
        dataFim: format(dateRange.endDate, 'yyyy-MM-dd'),
        limit: 9999
      },
      format,
      columns: currentReport.columns,
      filename: `relatorio_${reportType}_${format(new Date(), 'yyyyMMdd')}`,
      title: currentReport.label,
      subtitle: `Período: ${format(dateRange.startDate, 'dd/MM/yyyy')} a ${format(dateRange.endDate, 'dd/MM/yyyy')}`
    };

    exportData(reportType, config);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Gerador de Relatórios
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Configure e exporte relatórios personalizados
        </p>
      </div>

      {/* Configuração do Relatório */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Configuração do Relatório
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Tipo de Relatório */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo de Relatório
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              {reportTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Período */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Período
            </label>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </div>
        </div>

        {/* Filtros Adicionais */}
        {reportType === 'contratos' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status || 'todos'}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
              >
                <option value="todos">Todos</option>
                <option value="ativo">Ativo</option>
                <option value="suspenso">Suspenso</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Prévia do Relatório
          </h2>
          
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting || previewData.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar PDF
            </button>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting || previewData.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : previewData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {currentReport.columns.map((col, index) => (
                    <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {previewData.slice(0, 5).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {currentReport.columns.map((col, colIndex) => (
                      <td key={colIndex} className="px-4 py-2 text-sm">
                        {col.accessorKey.includes('.') 
                          ? col.accessorKey.split('.').reduce((obj, key) => obj?.[key], row)
                          : row[col.accessorKey]
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 5 && (
              <p className="text-center text-sm text-gray-500 py-2">
                Mostrando 5 de {previewData.length} registros
              </p>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            Nenhum dado encontrado para o período selecionado
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportBuilder;